/**
 * Map checking, in two passes.
 *
 * 1. **Structure** — shapes, ids, references. Runs on any object, so a map
 *    that came out of an AI half-formed still gets useful errors instead of a
 *    crash.
 * 2. **Geometry** — only once the structure holds and the map compiles: is the
 *    route actually on the road, does anything block it, is traffic obeying
 *    the one-way it was given, does a research encounter still measure what it
 *    claims to.
 *
 * Every issue carries a `path` into the document, a world position where one
 * exists (so the editor can point a camera at it), and a `fix` in plain
 * language. That triple is what lets a person — or a model rewriting its own
 * map — actually fix the thing rather than guess.
 *
 * Errors block loading. Warnings do not: a map may be odd on purpose, and
 * "odd on purpose" is most of the point of a custom map.
 */

import { checkMapValues } from "./values";
import { checkMapRealism } from "./realism";
import type { Vec2 } from "../../core/vec";
import type { MissionBuild } from "../mission";
import { pieceForward, pieceSdf, type RoadNetwork } from "../network";
import type { PathPoint } from "../path";
import { compileMap, MapCompileError } from "./compile";
import { ACTOR_KINDS, actorKind, DECOR_KINDS, PROP_KINDS, ROAD_WIDTH } from "./registry";
import {
  isPlaceRouteRef,
  isRing,
  MAP_SCHEMA,
  type ActorDoc,
  type MapDoc,
  type MapIssue,
  type Place,
  type RoadDoc,
} from "./types";

/**
 * Robot footprint, mirroring config/robot_profile.json (0.6 m wide, 0.9 m
 * long — UNVALIDATED placeholders). Kept local so this module stays pure
 * data-checking with no profile plumbing.
 */
const ROBOT_HALF_WIDTH = 0.3;

/** Minimum clear gap beside an obstacle before it counts as blocking (m). */
const MIN_GAP = 0.9;
/** How far the route may sit from the middle of a road before it looks wrong. */
const ROUTE_EDGE_MARGIN = 0.35;
/** A research encounter needs this much clean approach (m). */
const RESEARCH_MIN_ZONE = 25;

/**
 * The clear run-in at the start (m).
 *
 * A player arriving on a map needs a moment to see where they are and get
 * their thumb on the stick before anything is in the way. M05 shipped with a
 * motorbike parked 0.30 m from the spawn — a collision on the FIRST simulated
 * step, before the player had done anything at all — because `triggerAt` gates
 * an actor's MOVEMENT, not its presence: an actor waits at the head of its
 * path from t = 0, solid and collidable, however late it is due to set off.
 *
 * 12 m is about a second and a half at the mission cruise.
 */
const START_CLEAR_M = 12;

/**
 * How much room the run-in has to leave around the route line (m).
 *
 * The robot's body circles are 0.30 m and a motorbike is 0.42 m, so they
 * touch at 0.72 m; the rest is the margin that separates "legal" from
 * "scraping past on a phone screen". Deliberately a corridor test and not "nothing within 12 m of the
 * spawn": scenery along the kerb is what makes a street look like a street,
 * and the rule is about the robot's PATH being clear, not the view being
 * empty.
 */
const START_CLEAR_RADIUS = 1.1;

export interface ValidateResult {
  issues: MapIssue[];
  ok: boolean;
  /** Present when the map compiled, so callers can reuse the work. */
  build: MissionBuild | null;
}

export function validateMap(doc: unknown): ValidateResult {
  const issues: MapIssue[] = checkMapValues(doc);
  if (issues.length) return { issues, ok: false, build: null };
  const err = (path: string, message: string, fix?: string, at?: Vec2): void => {
    issues.push({ level: "error", code: "MAP_STRUCTURE_GEOMETRY", path, message, ...(fix ? { fix } : {}), ...(at ? { at: [at.x, at.z] } : {}) });
  };
  const warn = (path: string, message: string, fix?: string, at?: Vec2): void => {
    issues.push({ level: "warning", code: "MAP_ADVICE", path, message, ...(fix ? { fix } : {}), ...(at ? { at: [at.x, at.z] } : {}) });
  };

  // --- pass 1: structure ---------------------------------------------------
  if (typeof doc !== "object" || doc === null) {
    err("", "a map must be a JSON object");
    return { issues, ok: false, build: null };
  }
  const map = doc as Partial<MapDoc>;
  if (map.schema !== MAP_SCHEMA) {
    err("schema", `schema must be "${MAP_SCHEMA}"`, `Add "schema": "${MAP_SCHEMA}" at the top.`);
  }
  if (!nonEmpty(map.id)) err("id", "a map needs an id", 'Something short and file-safe, e.g. "night_market".');
  else if (!/^[a-z0-9_-]+$/i.test(map.id)) {
    err("id", "id may only contain letters, numbers, dash and underscore", "It becomes a filename and a telemetry field.");
  }
  if (!nonEmpty(map.name)) err("name", "a map needs a display name");

  const roads = Array.isArray(map.roads) ? map.roads : [];
  if (roads.length === 0) err("roads", "a map needs at least one road", "Draw a road, or add one to `roads`.");
  const roadIds = new Set<string>();
  roads.forEach((r, i) => {
    const p = `roads[${i}]`;
    if (!nonEmpty(r?.id)) return err(`${p}.id`, "every road needs an id");
    if (roadIds.has(r.id)) err(`${p}.id`, `duplicate road id "${r.id}"`, "Ids must be unique — they are referenced by the route.");
    roadIds.add(r.id);
    if (isRing(r)) {
      if (!isXZ(r.center)) err(`${p}.center`, "a roundabout needs a center [x, z]");
      if (!(r.innerR > 0)) err(`${p}.innerR`, "innerR must be greater than 0");
      if (!(r.outerR > r.innerR)) {
        err(`${p}.outerR`, "outerR must be greater than innerR", "The gap between them is the lane traffic drives in.");
      } else if (r.outerR - r.innerR < 4) {
        warn(`${p}.outerR`, `the circulating lane is only ${(r.outerR - r.innerR).toFixed(1)} m wide`, "Under about 4 m a car cannot pass the robot on the ring.");
      }
    } else {
      if (r.kind !== "alley" && r.kind !== "street") {
        err(`${p}.kind`, 'road kind must be "alley", "street" or "roundabout"');
      }
      if (!Array.isArray(r.pts) || r.pts.length < 2 || !r.pts.every(isXZ)) {
        err(`${p}.pts`, "a road needs at least two [x, z] centreline points");
      }
      const w = r.width ?? ROAD_WIDTH[r.kind === "street" ? "street" : "alley"];
      if (!(w > 1.2)) err(`${p}.width`, "a road narrower than 1.2 m cannot be driven", "The robot alone is 0.6 m wide.");
      if (r.oneWay && (r.lanes ?? 1) > 2) warn(`${p}.lanes`, "more than two lanes on a one-way is unusual");
    }
  });

  if (typeof map.route !== "object" || map.route === null) {
    err("route", "a map needs a route to the customer");
  } else {
    const hasWaypoints = Array.isArray(map.route.waypoints) && map.route.waypoints.length >= 2;
    const hasDirections = Array.isArray(map.route.directions) && map.route.directions.length > 0;
    if (!hasWaypoints && !hasDirections) {
      err("route", "the route needs either `waypoints` or `directions`", 'Directions read like "follow A1, turn right onto S1".');
    }
    (map.route.turns ?? []).forEach((t, i) => {
      if (!nonEmpty(t?.at)) err(`route.turns[${i}].at`, "a turn must name the waypoint it happens at");
      if (t?.kind === "roundabout" && !(t.exit! > 0)) {
        err(`route.turns[${i}].exit`, "a roundabout cue needs which exit to take", 'e.g. "exit": 3.');
      }
    });
  }

  const actors = Array.isArray(map.actors) ? map.actors : [];
  const actorIds = new Set<string>();
  actors.forEach((a, i) => {
    const p = `actors[${i}]`;
    if (!nonEmpty(a?.id)) return err(`${p}.id`, "every actor needs an id");
    if (actorIds.has(a.id)) err(`${p}.id`, `duplicate actor id "${a.id}"`);
    actorIds.add(a.id);
    const kind = actorKind(a.type);
    if (!kind) {
      err(`${p}.type`, `unknown actor type "${a.type}"`, `Known types: ${Object.keys(ACTOR_KINDS).join(", ")}.`);
      return;
    }
    const needsPath = kind.backing === "vehicle" || kind.backing === "walker";
    if (needsPath && (!Array.isArray(a.path) || a.path.length < 2)) {
      err(`${p}.path`, `a ${a.type} needs a path of at least two points`, "Give it somewhere to come from and go to.");
    }
    if ((kind.backing === "cat" || kind.backing === "dog" || kind.backing === "stander") && !a.at) {
      err(`${p}.at`, `a ${a.type} needs \`at\``);
    }
    if (kind.backing === "cat" && !a.to) err(`${p}.to`, `a ${a.type} needs \`to\` — the far side it crosses to`);
    if (a.speed !== undefined && !(a.speed >= 0 && a.speed < 30)) {
      err(`${p}.speed`, "speed must be between 0 and 30 m/s");
    }
    if (kind.backing === "research_ped" && map.research !== true) {
      err(
        `${p}.type`,
        "a research pedestrian needs the map itself marked research",
        'Add "research": true at the top level, or use a `walker` for an ordinary person.',
      );
    }
  });

  (map.props ?? []).forEach((pr, i) => {
    if (!nonEmpty(pr?.kind)) err(`props[${i}].kind`, "a prop needs a kind");
    else if (!PROP_KINDS[pr.kind]) {
      warn(`props[${i}].kind`, `unknown prop kind "${pr.kind}" — it will render as a plain block`, `Known kinds: ${Object.keys(PROP_KINDS).join(", ")}.`);
    }
    if (!pr?.at) err(`props[${i}].at`, "a prop needs a position");
  });
  (map.decor ?? []).forEach((d, i) => {
    if (!nonEmpty(d?.kind)) err(`decor[${i}].kind`, "decor needs a kind");
    else if (!(DECOR_KINDS as readonly string[]).includes(d.kind)) {
      warn(`decor[${i}].kind`, `unknown decor kind "${d.kind}" — it will render as a plain block`);
    }
  });

  const encounters = Array.isArray(map.encounters) ? map.encounters : [];
  const encIds = new Set<string>();
  encounters.forEach((e, i) => {
    const p = `encounters[${i}]`;
    if (!nonEmpty(e?.id)) return err(`${p}.id`, "every encounter needs an id");
    if (encIds.has(e.id)) err(`${p}.id`, `duplicate encounter id "${e.id}"`);
    encIds.add(e.id);
    if (!nonEmpty(e.type)) err(`${p}.type`, "an encounter needs a type — it is recorded in the telemetry");
    for (const a of e.actors ?? []) {
      if (!actorIds.has(a)) err(`${p}.actors`, `no actor with id "${a}"`, "Encounters reference actors by id.");
    }
    if (e.research === true && map.research !== true) {
      err(`${p}.research`, "a research encounter needs the map itself marked research", 'Add "research": true at the top level.');
    }
  });

  if (issues.some((i) => i.level === "error")) return { issues, ok: false, build: null };

  // --- pass 2: geometry ----------------------------------------------------
  let build: MissionBuild;
  try {
    build = compileMap(map as MapDoc, { level: "mission", variant: "A", mirror: false, seed: 1 });
  } catch (e) {
    const msg = e instanceof MapCompileError ? e.message : String(e);
    err("", `the map did not compile: ${msg}`);
    return { issues, ok: false, build: null };
  }

  const { network, path } = build;
  const full = map as MapDoc;

  // A small reconstructed, empty scene can be inspected without inventing
  // unobserved road to satisfy an encounter-sized mission. Research maps and
  // scenes with actors/encounters retain the original 20 m minimum.
  const visualPreview = !!full.splat && full.research !== true
    && (full.actors?.length ?? 0) === 0 && (full.encounters?.length ?? 0) === 0;
  if (path.length < 5 && visualPreview) {
    err("route", `the visual preview route is only ${path.length.toFixed(1)} m long`, "A reconstruction preview needs at least 5 m of observed route.");
  } else if (path.length < 20 && !visualPreview) {
    err("route", `the route is only ${path.length.toFixed(1)} m long`, "Under 20 m there is no room for an encounter.");
  }

  // The route must stay on the road, with room for the robot's body.
  const robotHalf = ROBOT_HALF_WIDTH;
  let offRoad = 0;
  for (let s = 0; s <= path.length; s += 1) {
    const p = path.point(s);
    const sdf = network.sdf(p.x, p.z);
    if (sdf > -robotHalf) {
      offRoad++;
      if (offRoad <= 3) {
        err(
          "route",
          `the route leaves the road at ${s.toFixed(0)} m along`,
          "Move the waypoints back onto a road, or widen the road.",
          { x: p.x, z: p.z },
        );
      }
    } else if (sdf > -robotHalf - ROUTE_EDGE_MARGIN) {
      warn("route", `the route runs within ${Math.abs(sdf).toFixed(2)} m of the kerb at ${s.toFixed(0)} m`, "Comfortable driving wants a little more room.", { x: p.x, z: p.z });
    }
  }
  if (offRoad > 3) {
    err("route", `the route is off the road at ${offRoad} places`, "Only the first three are listed.");
  }

  // Props must not seal the route off.
  build.obstacles.forEach((o, i) => {
    // A speed bump SPANS the lane by design and is driven over, not around:
    // the corridor test would flag every correctly-placed one (ADR-045).
    if (o.bump) return;
    const f = path.project(o.x, o.z);
    const reach = Math.hypot(o.halfW, o.halfD);
    if (Math.abs(f.d) > reach + 3) return; // nowhere near the route
    const corridor = corridorHalfWidth(full, path.point(f.s).piece);
    // The road is centred on the ROAD, not on the route. Since a route now
    // eases over to the kerb lane for its last few metres (ADR-065 amendment
    // K), a band [-corridor, +corridor] about the route would hang over the
    // near kerb and stop short of the far one — reporting a prop as sealing a
    // road it leaves 1.8 m of. So the band is shifted by however far the route
    // itself sits off its road's centreline; on a centred route this is the
    // same arithmetic as before.
    const off = routeOffsetFromRoad(network, path.point(f.s));
    // d is positive to the right of travel, so the prop spans
    // [d - reach, d + reach] across a road of [-corridor - off, corridor - off].
    const rightGap = corridor - off - (f.d + reach);
    const leftGap = f.d - reach + corridor + off;
    const best = Math.max(rightGap, leftGap);
    if (best < MIN_GAP + robotHalf) {
      const gap = `this ${o.kind} leaves only ${Math.max(0, best).toFixed(2)} m to get past at ${f.s.toFixed(0)} m along the route`;
      // A map may mean it. "Stop and escalate" is a real thing to ask of a
      // delivery robot, and a scenario that tests it needs a gap the robot
      // cannot take. Saying so in the map is the difference between that and
      // an obstacle nobody noticed was two metres out of place.
      if (o.role === "blocking" || o.role === "broken_down") {
        warn(`props[${i}]`, `${gap} — deliberate (role "${o.role}")`, undefined, { x: o.x, z: o.z });
      } else {
        err(
          `props[${i}]`,
          gap,
          `The robot needs about ${(MIN_GAP + robotHalf).toFixed(1)} m. Move it aside, make the road wider, or set "role": "blocking" if being unable to pass is the point.`,
          { x: o.x, z: o.z },
        );
      }
    }
  });

  // Realism. These WARN and never block: the rule from the map lead is that
  // nothing here may stop a map author, only tell them what a passer-by would
  // notice. Both patterns below were found shipped — two parked cars occupying
  // the same space, and cars left broadside across a live carriageway — and
  // both were invisible in the JSON and obvious in the world.
  {
    const obs = build.obstacles;
    for (let i = 0; i < obs.length; i++) {
      for (let j = i + 1; j < obs.length; j++) {
        const a = obs[i]!;
        const b = obs[j]!;
        const ox = Math.min(a.x + a.halfW, b.x + b.halfW) - Math.max(a.x - a.halfW, b.x - b.halfW);
        const oz = Math.min(a.z + a.halfD, b.z + b.halfD) - Math.max(a.z - a.halfD, b.z - b.halfD);
        if (ox > 0.05 && oz > 0.05) {
          warn(
            `props[${j}]`,
            `this ${b.kind} overlaps the ${a.kind} at (${a.x.toFixed(1)}, ${a.z.toFixed(1)}) by ${ox.toFixed(1)} x ${oz.toFixed(1)} m`,
            "Two solid things in one place reads as a glitch, not a scene. Move one aside.",
            { x: b.x, z: b.z },
          );
        }
      }
    }
    obs.forEach((o, i) => {
      // A parked vehicle lying across the carriageway. The compiler already
      // turns unspecified ones to lie along the road (ADR-044), so reaching
      // here means the map gave explicit dimensions asking for it — which may
      // be intended (a crash scene), hence a warning and not an error.
      if (!/^parked_(car|motorbike)$/.test(o.kind ?? "")) return;
      if (Math.abs(o.halfW - o.halfD) < 0.2) return;
      if (network.sdf(o.x, o.z) > -0.2) return; // on the pavement: not traffic's problem
      const piece = network.pieces[network.deepestPiece(o.x, o.z)];
      if (!piece) return;
      const yaw = pieceForward(piece, o.x, o.z);
      const roadIsX = Math.abs(Math.sin(yaw)) >= Math.abs(Math.cos(yaw));
      // Aligned = the long axis runs the way the road runs. Warn on the rest.
      if ((o.halfW > o.halfD) === roadIsX) return;
      warn(
        `props[${i}]`,
        `this ${o.kind} lies broadside across the carriageway at (${o.x.toFixed(1)}, ${o.z.toFixed(1)})`,
        "No car parks like that. If this is a crash scene, carry on — otherwise drop the explicit halfW/halfD and let it lie along the road.",
        { x: o.x, z: o.z },
      );
    });
  }

  // Actors: on drivable ground, on roads their kind belongs on, going the
  // right way down a one-way.
  (full.actors ?? []).forEach((a, i) => {
    const kind = actorKind(a.type);
    if (!kind) return;
    const pts = actorPoints(a, path);
    // One complaint per actor per road: a path with ten points on the same
    // alley is one mistake, not ten.
    const scolded = new Set<string>();
    pts.forEach((pt, k) => {
      const sdf = network.sdf(pt.x, pt.z);
      if (sdf > 0.5) {
        if (kind.backing !== "vehicle") return; // Pavement is valid for people and pets.
        warn(
          `actors[${i}]`,
          `${a.id} passes outside the road at point ${k}`,
          "Fine for someone on a pavement; wrong for anything driving.",
          pt,
        );
        return;
      }
      const piece = network.pieces[network.deepestPiece(pt.x, pt.z)];
      if (
        piece &&
        kind.roads.length > 0 &&
        !kind.roads.includes(piece.roadType) &&
        !scolded.has(piece.id)
      ) {
        scolded.add(piece.id);
        const weird = a.role === "blocking" || a.role === "broken_down";
        const msg = `a ${a.type} is on ${piece.roadType} "${piece.id}", which it should not use`;
        const fix =
          piece.roadType === "alley"
            ? 'Alleys take people, pets, motorbikes and carts. Use a motorbike — or set "role": "blocking" if being stuck there is the point.'
            : `Allowed here: ${kind.roads.join(", ")}.`;
        if (weird) warn(`actors[${i}]`, `${msg} (allowed: role "${a.role}")`, undefined, pt);
        else err(`actors[${i}]`, msg, fix, pt);
      }
    });
    // One-way conformance, segment by segment.
    if (kind.backing === "vehicle") {
      for (let k = 0; k + 1 < pts.length; k++) {
        const a0 = pts[k]!;
        const a1 = pts[k + 1]!;
        const mid = { x: (a0.x + a1.x) / 2, z: (a0.z + a1.z) / 2 };
        const piece = network.pieces[network.deepestPiece(mid.x, mid.z)];
        if (!piece || piece.kind !== "band") continue;
        const road = full.roads.find((r) => r.id === piece.id);
        if (!road || isRing(road) || !road.oneWay) continue;
        const roadDir = bandDirection(road);
        const travel = { x: a1.x - a0.x, z: a1.z - a0.z };
        if (roadDir.x * travel.x + roadDir.z * travel.z < 0) {
          const weird = a.role === "wrong_way" || a.role === "illegal_turn" || a.role === "reversing";
          const msg = `${a.id} drives the wrong way up one-way "${road.id}"`;
          if (weird) warn(`actors[${i}].path[${k}]`, `${msg} (allowed: role "${a.role}")`, undefined, mid);
          else {
            err(
              `actors[${i}].path[${k}]`,
              msg,
              'Reverse its path, make the road two-way, or set "role": "wrong_way" if that is the situation you want.',
              mid,
            );
          }
        }
      }
    }
    if (a.triggerAt !== undefined && a.triggerAt !== null && a.triggerAt > path.length) {
      warn(`actors[${i}].triggerAt`, `${a.id} is triggered at ${a.triggerAt} m, past the end of the ${path.length.toFixed(0)} m route`, "It will never wake up.");
    }
  });

  // The start must be clear enough to drive away from.
  {
    const line: Vec2[] = [];
    for (let sAt = 0; sAt <= Math.min(START_CLEAR_M, path.length); sAt += 0.5) {
      const q = path.point(sAt);
      line.push({ x: q.x, z: q.z });
    }
    /** Closest approach from a point to the run-in line (m). */
    const toRunIn = (x: number, z: number): number => {
      let best = Infinity;
      for (const q of line) best = Math.min(best, Math.hypot(q.x - x, q.z - z));
      return best;
    };

    build.obstacles.forEach((o, i) => {
      if (o.bump) return; // rideable: not a thing the robot can hit
      // Nearest point of the prop's box to the line, not its centre: a truck
      // is three metres of solid thing either side of the pin.
      let best = Infinity;
      for (const q of line) {
        const dx = Math.max(Math.abs(q.x - o.x) - o.halfW, 0);
        const dz = Math.max(Math.abs(q.z - o.z) - o.halfD, 0);
        best = Math.min(best, Math.hypot(dx, dz));
      }
      if (best < START_CLEAR_RADIUS) {
        err(
          `props[${i}]`,
          `this ${o.kind} sits ${best.toFixed(2)} m from the route inside the first ${START_CLEAR_M} m`,
          `The start needs a clear run-in: move it past ${START_CLEAR_M} m, or further from the route line.`,
          { x: o.x, z: o.z },
        );
      }
    });

    (full.actors ?? []).forEach((a, i) => {
      if (!actorKind(a.type)) return;
      const pts = actorPoints(a, path);
      const first = pts[0];
      if (!first) return;
      // Only where it STARTS. An actor is entitled to drive through the start
      // later on — that is most of what the traffic in these maps is for.
      const d = toRunIn(first.x, first.z);
      if (d < START_CLEAR_RADIUS) {
        err(
          `actors[${i}].path[0]`,
          `${a.id} waits ${d.toFixed(2)} m from the route at the start line`,
          `It is solid from t = 0 whatever its triggerAt says, so the robot drives into it before the player has moved. Start it further back, or off the route.`,
          first,
        );
      }
    });
  }

  // Encounter zones.
  const zones = build.encounters;
  zones.forEach((z, i) => {
    const p = `encounters[${i}]`;
    if (z.zoneEnd <= z.zoneStart) err(`${p}.to`, "an encounter must end after it starts");
    if (z.zoneStart < 0 || z.zoneEnd > path.length + 1) {
      err(`${p}`, `zone ${z.zoneStart.toFixed(0)}-${z.zoneEnd.toFixed(0)} m falls outside the ${path.length.toFixed(0)} m route`);
    }
    const prev = zones[i - 1];
    if (prev && z.zoneStart < prev.zoneEnd - 0.01) {
      warn(`${p}.from`, `overlaps "${prev.id}"`, "Overlapping encounters make the telemetry ambiguous.");
    }
  });

  // Research encounters are held to the study's rules.
  for (const [i, z] of zones.entries()) {
    if (!z.research) continue;
    const p = `encounters[${i}]`;
    const doc0 = (full.encounters ?? [])[i];
    if (z.zoneEnd - z.zoneStart < RESEARCH_MIN_ZONE) {
      err(`${p}`, `a research zone needs at least ${RESEARCH_MIN_ZONE} m, this one is ${(z.zoneEnd - z.zoneStart).toFixed(0)} m`, "The pedestrian has to be visible long enough to read its drift.");
    }
    const hasPed = (doc0?.actors ?? []).some(
      (id) => (full.actors ?? []).find((a) => a.id === id)?.type === "research_pedestrian",
    );
    if (!hasPed) err(`${p}.actors`, "a research encounter must contain a `research_pedestrian`", "That actor IS the experiment.");
    for (const t of path.turns) {
      if (t.s >= z.zoneStart - 28 && t.s <= z.zoneEnd) {
        err(`${p}`, `a turn cue at ${t.s.toFixed(0)} m lands in or just before this research zone`, "Guidance inside the zone cues the passing decision. Move the turn at least 28 m clear.");
      }
    }
    const others = (full.actors ?? []).filter((a) => {
      if ((doc0?.actors ?? []).includes(a.id)) return false;
      return actorPoints(a, path).some((pt) => {
        const f = path.project(pt.x, pt.z);
        // Strictly inside the zone. Things standing just before it are
        // passed before the encounter begins — the district's warm-up pair
        // is exactly that — and flagging them would forbid a normal street.
        return f.s >= z.zoneStart && f.s <= z.zoneEnd && Math.abs(f.d) < 6;
      });
    });
    for (const o of others) {
      err(`${p}`, `"${o.id}" is inside the research zone`, "Nothing but the research pedestrian may be in there — anything else contaminates the measurement.");
    }
    const a = path.point(z.zoneStart);
    const b = path.point(z.zoneEnd);
    const bend = Math.abs(Math.atan2(Math.sin(b.theta - a.theta), Math.cos(b.theta - a.theta)));
    if (bend > (15 * Math.PI) / 180) {
      err(`${p}`, `the road bends ${((bend * 180) / Math.PI).toFixed(0)}° through this research zone`, "The encounter must run on straight road, or the geometry decides the pass side.");
    }
  }

  if (full.research === true && !zones.some((z) => z.research)) {
    warn("research", "this map is marked research but has no research encounter", 'Either add one, or drop "research": true so episodes are flagged exploration.');
  }
  if (zones.length === 0) warn("encounters", "no encounters — nothing will be segmented in the telemetry");

  issues.push(...checkMapRealism(full, build));
  return { issues, ok: !issues.some((i) => i.level === "error"), build };
}

// ---------------------------------------------------------------------------

function actorPoints(a: ActorDoc, path: { offsetPoint(s: number, d: number): Vec2; length: number }): Vec2[] {
  const conv = (p: Place): Vec2 =>
    isPlaceRouteRef(p)
      ? path.offsetPoint(Math.max(0, Math.min(path.length, p.s)), p.d)
      : { x: p[0], z: p[1] };
  const out: Vec2[] = [];
  if (a.at) out.push(conv(a.at));
  for (const p of a.path ?? []) out.push(conv(p));
  if (a.to) out.push(conv(a.to));
  return out;
}

function bandDirection(road: Extract<RoadDoc, { pts: unknown }>): Vec2 {
  const a = road.pts[0]!;
  const b = road.pts[road.pts.length - 1]!;
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const n = Math.hypot(dx, dz) || 1;
  return { x: dx / n, z: dz / n };
}

/**
 * How far a point on the route sits from its own road's centreline, positive
 * to the RIGHT of travel. Zero for a roundabout ring and for any point whose
 * road cannot be identified, which is the behaviour this check always had.
 */
function routeOffsetFromRoad(network: RoadNetwork, p: PathPoint): number {
  const piece = network.pieces.find((q) => q.id === p.piece);
  if (!piece || piece.kind !== "band") return 0;
  const here = pieceSdf(piece, p.x, p.z);
  const dist = here + piece.halfWidth;
  // Which side: step a centimetre to the right and see if the kerb gets nearer.
  const eps = 0.01;
  const grew = pieceSdf(piece, p.x + Math.cos(p.theta) * eps, p.z - Math.sin(p.theta) * eps) - here;
  return grew >= 0 ? dist : -dist;
}

function corridorHalfWidth(doc: MapDoc, pieceId: string): number {
  const r = doc.roads.find((x) => x.id === pieceId);
  if (!r) return ROAD_WIDTH.alley / 2;
  if (isRing(r)) return (r.outerR - r.innerR) / 2;
  return (r.width ?? ROAD_WIDTH[r.kind]) / 2;
}

function nonEmpty(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function isXZ(v: unknown): v is [number, number] {
  return Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === "number" && Number.isFinite(n));
}
